import { useState, useEffect, useCallback } from 'react';
import { C, F, GREEN } from '../constants';

const API_URL = process.env.REACT_APP_MEDIA_STUDIO_API_URL || 'https://strategic-honesty-media-studio-production.up.railway.app';
const API_KEY = process.env.REACT_APP_API_KEY;

// Shows content that got flagged instead of silently deleted — either by
// the media-quality scan (dead/oversized files) or by the weekly
// scheduler's repeated-failure guard (see weeklyScheduler.js's
// quarantineQueueItem). Nothing here was destroyed; every row can be
// restored to the normal publish queue or permanently discarded by hand.
export default function QuarantinePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchQuarantined = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/buffer-queue/quarantined`, { headers: { 'x-api-key': API_KEY } });
      const data = await res.json();
      setItems(data.success ? (data.posts || []) : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQuarantined(); }, [fetchQuarantined]);

  async function restore(id) {
    setBusyId(id);
    try {
      await fetch(`${API_URL}/api/buffer-queue/${id}/restore`, { method: 'PATCH', headers: { 'x-api-key': API_KEY } });
      await fetchQuarantined();
    } finally {
      setBusyId(null);
    }
  }

  async function permanentlyDelete(id) {
    if (!window.confirm('Permanently delete this item? This cannot be undone.')) return;
    setBusyId(id);
    try {
      await fetch(`${API_URL}/api/buffer-queue/${id}`, { method: 'DELETE', headers: { 'x-api-key': API_KEY } });
      await fetchQuarantined();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 16 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>🔒</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Quarantine — Needs Review</span>
          <span style={{ fontSize: 11, color: '#fff', background: items.length ? '#dc2626' : '#94a3b8', borderRadius: 10, padding: '1px 8px', fontWeight: 600 }}>{items.length}</span>
        </div>
        <span style={{ fontSize: 12, color: C.muted }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          {loading ? (
            <div style={{ fontSize: 12, color: C.muted }}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>Nothing quarantined — flagged content will show up here for review instead of being silently deleted.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {items.map(item => (
                <div key={item.id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, background: '#fef2f2' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#fee2e2', color: '#dc2626' }}>{item.platform}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{item.title || '(untitled)'}</span>
                    <span style={{ fontSize: 10, color: C.muted, marginLeft: 'auto' }}>{item.quarantined_at ? new Date(item.quarantined_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#991b1b', marginBottom: 8 }}>Why: {item.quarantine_reason}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => restore(item.id)} disabled={busyId === item.id} style={{ padding: '6px 14px', background: busyId === item.id ? '#e0e0e0' : GREEN, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: busyId === item.id ? 'not-allowed' : 'pointer', fontFamily: F }}>
                      ↩ Restore to queue
                    </button>
                    <button onClick={() => permanentlyDelete(item.id)} disabled={busyId === item.id} style={{ padding: '6px 14px', background: '#fff', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: busyId === item.id ? 'not-allowed' : 'pointer', fontFamily: F }}>
                      🗑 Delete permanently
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
